import { useEffect, useState } from "react";
import { get } from "../api.js";
import {
  diaryPaymentClassifications,
  diaryPaymentStateLabel,
  type DiaryPaymentClassification,
} from "../booking/diaryPayments.js";
import {
  STUDENT_CREDIT_STATE_CHANGED_EVENT,
  studentCreditStateChangedFor,
} from "../booking/studentCreditEvents.js";
import { formatDate } from "../format.js";
import { Pagination } from "../Pagination.js";
import { DENSE_ROW_PAGE_SIZE } from "../paginationPageSizes.js";
import { TableScrollRegion } from "../ui/TableScrollRegion.js";
import { useAsync } from "../useAsync.js";

export interface StudentLessonHistoryItem {
  /** Absent on older Hub versions; such rows cannot be selected for changes. */
  bookingId?: string | null;
  historyId: string;
  appointmentId: string | null;
  ameliaAppointmentId: number | null;
  startsAt: string;
  endsAt: string | null;
  serviceName: string | null;
  status: string;
  bookingStatus: string;
  tutorName: string | null;
  attendanceStatus: string | null;
}

interface StudentLessonHistoryPage {
  lessons: StudentLessonHistoryItem[];
  page: number;
  limit: number;
  total: number;
}

export function studentLessonWasCancelled(
  lesson: Pick<StudentLessonHistoryItem, "status" | "bookingStatus">,
): boolean {
  return lesson.status === "canceled" || lesson.bookingStatus === "canceled";
}

export function studentLessonAttendanceLabel(
  lesson: Pick<
    StudentLessonHistoryItem,
    "status" | "bookingStatus" | "attendanceStatus"
  >,
): string {
  if (studentLessonWasCancelled(lesson)) return "Cancelled";
  if (lesson.status === "no-show" || lesson.bookingStatus === "no-show") return "No-show";
  if (lesson.status === "rejected" || lesson.bookingStatus === "rejected") return "Rejected";
  if (!lesson.attendanceStatus) return "—";
  return `${lesson.attendanceStatus.charAt(0).toUpperCase()}${lesson.attendanceStatus.slice(1)}`;
}

function paymentLabel(
  lesson: StudentLessonHistoryItem,
  payment: DiaryPaymentClassification | undefined,
  paymentLoading: boolean,
  paymentError: string | null,
): string {
  if (lesson.ameliaAppointmentId === null) return "—";
  if (paymentError) return "Unavailable";
  if (paymentLoading) return "Loading…";
  return diaryPaymentStateLabel(payment, lesson.endsAt) ?? "—";
}

export function StudentLessonHistory({ studentId }: { studentId: string }) {
  const [page, setPage] = useState(1);
  const [hideCancelled, setHideCancelled] = useState(false);

  useEffect(() => {
    setPage(1);
    setHideCancelled(false);
  }, [studentId]);

  const history = useAsync(
    () =>
      get<StudentLessonHistoryPage>(`/admin/students/${studentId}/lesson-history`, {
        page: String(page),
        limit: String(DENSE_ROW_PAGE_SIZE),
        excludeCancelled: hideCancelled ? "true" : undefined,
      }),
    [studentId, page, hideCancelled],
    { reloadOnMirrorRefresh: true },
  );

  const paymentIds =
    history.loading || history.error
      ? []
      : (history.data?.lessons ?? []).flatMap((lesson) =>
          lesson.ameliaAppointmentId === null ? [] : [lesson.ameliaAppointmentId],
        );
  const paymentIdsKey = paymentIds.join(",");
  const payments = useAsync(
    () => diaryPaymentClassifications(paymentIds),
    [paymentIdsKey],
    { reloadOnMirrorRefresh: true },
  );

  useEffect(() => {
    const onStudentCreditStateChanged = (event: Event) => {
      if (!studentCreditStateChangedFor(event, studentId)) return;
      history.reload();
      payments.reload();
    };
    window.addEventListener(
      STUDENT_CREDIT_STATE_CHANGED_EVENT,
      onStudentCreditStateChanged,
    );
    return () =>
      window.removeEventListener(
        STUDENT_CREDIT_STATE_CHANGED_EVENT,
        onStudentCreditStateChanged,
      );
  }, [history.reload, payments.reload, studentId]);

  const paymentsByAppointment = new Map(
    (payments.data ?? []).map((payment) => [payment.appointment_id, payment]),
  );

  const loaded = !history.loading && history.error === null ? history.data : null;
  const pages = loaded ? Math.max(1, Math.ceil(loaded.total / loaded.limit)) : 1;
  const currentPage = loaded ? Math.min(loaded.page, pages) : page;

  return (
    <section aria-labelledby="student-lesson-history-heading">
      <div className="student-record-section-head">
        <div>
          <h2 id="student-lesson-history-heading">Lesson history</h2>
          <p className="small muted">All previous lessons, newest first.</p>
        </div>
        <label className="small">
          <input
            type="checkbox"
            checked={hideCancelled}
            onChange={(event) => {
              setPage(1);
              setHideCancelled(event.currentTarget.checked);
            }}
          />{" "}
          Hide cancelled lessons
        </label>
      </div>

      <div className="panel student-record-table-panel">
        {history.loading && <p className="empty">Loading lesson history…</p>}
        {!history.loading && history.error && <div className="error">{history.error}</div>}
        {loaded && loaded.lessons.length === 0 && (
          <p className="empty">
            {hideCancelled ? "No non-cancelled lessons recorded yet." : "No lessons recorded yet."}
          </p>
        )}
        {loaded && loaded.lessons.length > 0 && (
          <>
            <TableScrollRegion label="Student lesson history" minWidth="680px">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Lesson</th>
                    <th>Attendance</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.lessons.map((lesson) => (
                    <tr key={lesson.historyId}>
                      <td className="tabular">{formatDate(lesson.startsAt)}</td>
                      <td>{lesson.serviceName ?? "Lesson"}</td>
                      <td className="muted small">{studentLessonAttendanceLabel(lesson)}</td>
                      <td className="muted small">
                        {paymentLabel(
                          lesson,
                          lesson.ameliaAppointmentId === null
                            ? undefined
                            : paymentsByAppointment.get(lesson.ameliaAppointmentId),
                          payments.loading,
                          payments.error,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScrollRegion>
            <Pagination
              page={currentPage}
              pages={pages}
              onPageChange={setPage}
              ariaLabel="Lesson history pagination"
            />
          </>
        )}
      </div>
    </section>
  );
}
